<template>
  <div class="item-web-option-mappings">
    <v-card outlined class="web-mappings-card">
      <v-card-title class="web-mappings-header">
        <span class="headline web-mappings-title">Web option mappings</span>
        <v-spacer></v-spacer>
        <v-btn small color="primary" class="mr-2 web-mappings-action" @click="generateMissingMappings">
          Generate missing mappings
        </v-btn>
        <v-btn small color="success" class="web-mappings-action" @click="addEmptyMapping">
          New mapping
        </v-btn>
      </v-card-title>

      <v-card-text class="web-mappings-body">
        <v-alert type="info" dense text class="web-mappings-alert">
          Here you can connect selected webshop option combinations to the concrete ERP item.
          Example: Color = Blue + Length = 3000 mm -> KM-T8-BLUE-3000.
        </v-alert>

        <div class="web-mappings-panel">
          <v-row class="mapping-toolbar-row">
            <v-col cols="12" md="8">
              <v-select
                v-model="selectedMappingKeys"
                :items="availableMappingOptions"
                label="Options used for mapping"
                item-text="label"
                item-value="key"
                multiple
                small-chips
                deletable-chips
                clearable
                hint="Only these option keys will be used to find the target ERP item. Do not include custom_length if it is only a custom user value."
                persistent-hint
              />
            </v-col>
            <v-col cols="12" md="4" class="mapping-toolbar-actions">
              <v-btn small color="primary" class="mr-2 mapping-toolbar-button" @click="generateMissingMappings">
                Generate from values
              </v-btn>
              <v-btn small text class="mapping-toolbar-button" @click="removeInactiveMappings" :disabled="!localMappings.length">
                Remove inactive
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <v-alert v-if="!availableMappingOptions.length" type="warning" dense text class="web-mappings-alert">
          There are no selectable options yet. Create at least one Dropdown / Radio option with values first.
        </v-alert>

        <v-alert v-if="localMappings.length && hasInvalidMappings" type="warning" dense text class="web-mappings-alert">
          Some mappings have no target item selected. These combinations should not be orderable until a target item is set.
        </v-alert>

        <div class="mapping-table-wrap">
          <v-simple-table class="elevation-0 web-option-mapping-table">
          <thead>
            <tr>
              <th
                v-for="option in selectedMappingOptions"
                :key="option.key"
                class="text-left mapping-option-column"
              >
                {{ getLocalizedOptionLabel(option) }}
              </th>
              <th class="text-left target-item-column">Target item</th>
              <th class="text-left target-reference-column">Target reference</th>
              <th class="text-center small-column">Custom</th>
              <th class="text-center small-column">Active</th>
              <th class="text-center action-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!localMappings.length">
              <td :colspan="selectedMappingOptions.length + 5" class="text-center text-muted py-5 empty-mappings-cell">
                No mappings yet. Select mapping options and click "Generate missing mappings".
              </td>
            </tr>

            <tr
              v-for="(mapping, index) in paginatedMappings"
              :key="mapping._uid"
              :class="{ 'mapping-row-inactive': !mapping.is_active }"
              :id="mappingPageStartIndex + index"
            >
              <td
                v-for="option in selectedMappingOptions"
                :key="option.key"
                class="mapping-option-cell"
              >
                <v-select
                  :value="getMappingOptionValue(mapping, option.key)"
                  :items="getOptionValues(option)"
                  :item-text="getLocalizedValueLabel"
                  item-value="value"
                  dense
                  hide-details
                  clearable
                  class="mapping-control"
                  @change="setMappingOptionValue(mapping, option.key, $event)"
                />
              </td>

              <td class="mapping-target-cell">
                <v-autocomplete
                  v-model="mapping.target_item_id"
                  :items="itemCollection"
                  item-text="ReferenceName"
                  item-value="id"
                  dense
                  hide-details
                  clearable
                  placeholder="Select item"
                  class="mapping-control mapping-control--target"
                  @change="handleTargetItemChange(mapping)"
                >
                  <template v-slot:item="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ getItemDisplayName(data.item) }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="data">
                    {{ getItemDisplayName(data.item) }}
                  </template>
                </v-autocomplete>
              </td>

              <td class="mapping-reference-cell">
                <v-text-field
                  v-model="mapping.target_reference"
                  dense
                  hide-details
                  placeholder="KM-T8-..."
                  class="mapping-control mapping-control--reference"
                />
              </td>

              <td class="text-center mapping-flag-cell">
                <v-checkbox
                  v-model="mapping.is_custom"
                  dense
                  hide-details
                  class="justify-center mt-0 mapping-toggle"
                  color="primary"
                  @change="emitChange"
                />
              </td>

              <td class="text-center mapping-flag-cell">
                <v-switch
                  v-model="mapping.is_active"
                  dense
                  hide-details
                  class="justify-center mt-0 mapping-toggle"
                  color="success"
                  @change="emitChange"
                />
              </td>

              <td class="text-center mapping-actions-cell">
                <v-icon small class="mr-2 mapping-action-icon" title="Duplicate" @click="duplicateMapping(mapping)">
                  mdi-content-copy
                </v-icon>
                <v-icon small class="mapping-action-icon mapping-action-icon--danger" title="Delete" @click="deleteMapping(mapping)">
                  mdi-delete
                </v-icon>
              </td>
            </tr>
          </tbody>
          </v-simple-table>
        </div>

        <div v-if="sortedMappings.length" class="mapping-pagination-row">
          <div class="caption grey--text mapping-pagination-info">
            Showing {{ mappingShowingFrom }}-{{ mappingShowingTo }} of {{ sortedMappings.length }} mappings
          </div>
          <v-spacer></v-spacer>
          <v-select
            v-model="mappingItemsPerPage"
            :items="mappingItemsPerPageOptions"
            label="Rows per page"
            dense
            hide-details
            outlined
            class="mapping-per-page-select"
          />
          <v-pagination
            v-model="mappingPage"
            :length="mappingPageCount"
            :total-visible="7"
            class="mapping-pagination"
          />
        </div>

        <v-divider class="web-mappings-divider"></v-divider>

        <div class="caption grey--text web-mappings-note">
          Mapping uses only the selected option keys above. Extra options are ignored here. For example, if custom length is a number typed by the customer,
          do not use custom_length in the mapping. Use length = custom and save the typed custom_length only on the cart/order line.
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Swal from 'sweetalert2';

const initialMapping = () => ({
  id: null,
  base_item_id: null,
  selected_options_json: {},
  option_hash: null,
  target_item_id: null,
  target_reference: '',
  is_custom: false,
  is_active: true,
});

export default {
  name: 'ItemWebOptionMappings',
  props: {
    value: {
      type: Array,
      default: function () {
        return [];
      },
    },
    options: {
      type: Array,
      default: function () {
        return [];
      },
    },
    itemCollection: {
      type: Array,
      default: function () {
        return [];
      },
    },
    baseItemId: {
      type: [Number, String],
      default: null,
    },
    selectedLocale: {
      type: [Object, String],
      default: function () {
        return { lang: 'hu' };
      },
    },
  },
  data() {
    return {
      localMappings: [],
      selectedMappingKeys: [],
      uidCounter: 1,
      mappingPage: 1,
      mappingItemsPerPage: 10,
      mappingItemsPerPageOptions: [10, 20, 50, 100],
    };
  },
  computed: {
    activeLocale() {
      const locale = typeof this.selectedLocale === 'string'
        ? this.selectedLocale
        : (this.selectedLocale && this.selectedLocale.lang);

      return this.normalizeLocale(locale || 'hu');
    },
    availableMappingOptions() {
      return this.normalizedOptions
        .filter((option) => {
          return this.hasOptionTranslation(option, this.activeLocale)
            && !option.is_extra
            && ['select', 'radio', 'checkbox'].includes(option.type)
            && this.getOptionValues(option).length > 0;
        })
        .map((option) => {
          return {
            key: option.key,
            label: this.getExactTranslationField(option, 'label', this.activeLocale) + ' (' + option.key + ')',
          };
        });
    },
    selectedMappingOptions() {
      return this.normalizedOptions.filter((option) => {
        return this.selectedMappingKeys.includes(option.key)
          && this.hasOptionTranslation(option, this.activeLocale);
      });
    },
    normalizedOptions() {
      return this.clone(this.options || [])
        .map((option) => this.normalizeOption(option))
        .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    },
    sortedMappings() {
      return this.localMappings.slice().sort((a, b) => {
        return this.buildMappingKey(a.selected_options_json).localeCompare(this.buildMappingKey(b.selected_options_json));
      });
    },
    mappingPageStartIndex() {
      return (this.mappingPage - 1) * this.mappingItemsPerPage;
    },
    paginatedMappings() {
      return this.sortedMappings.slice(this.mappingPageStartIndex, this.mappingPageStartIndex + this.mappingItemsPerPage);
    },
    mappingPageCount() {
      return Math.max(1, Math.ceil(this.sortedMappings.length / this.mappingItemsPerPage));
    },
    mappingShowingFrom() {
      if (!this.sortedMappings.length) {
        return 0;
      }

      return this.mappingPageStartIndex + 1;
    },
    mappingShowingTo() {
      return Math.min(this.mappingPageStartIndex + this.mappingItemsPerPage, this.sortedMappings.length);
    },
    hasInvalidMappings() {
      return this.localMappings.some((mapping) => {
        return mapping.is_active && (!mapping.target_item_id || !mapping.target_reference);
      });
    },
  },
  watch: {
    value: {
      handler(value) {
        this.localMappings = this.clone(value || []).map((mapping) => this.normalizeMapping(mapping));
      },
      immediate: true,
    },
    availableMappingOptions: {
      handler(options) {
        if (this.selectedMappingKeys.length) {
          this.selectedMappingKeys = this.selectedMappingKeys.filter((key) => {
            return options.some((option) => option.key === key);
          });
          return;
        }

        this.selectedMappingKeys = options.map((option) => option.key);
      },
      immediate: true,
    },
    options: {
      handler() {
        this.ensureMappingsHaveSelectedKeys();
      },
      deep: true,
    },
    selectedMappingKeys() {
      this.ensureMappingsHaveSelectedKeys();
      this.mappingPage = 1;
    },
    mappingItemsPerPage() {
      this.mappingPage = 1;
      this.clampMappingPage();
    },
    mappingPageCount(value) {
      if (this.mappingPage > value) {
        this.mappingPage = value;
      }
    },
  },
  methods: {
    clone(data) {
      return JSON.parse(JSON.stringify(data || []));
    },
    normalizeLocale(locale) {
      return String(locale || 'hu').trim().toLowerCase().replace('_', '-');
    },
    getExactTranslationField(target, field, locale) {
      const normalizedLocale = this.normalizeLocale(locale || this.activeLocale);
      const translations = target && target.translations && typeof target.translations === 'object' && !Array.isArray(target.translations)
        ? target.translations
        : {};
      const translation = translations[normalizedLocale];

      if (!translation || translation[field] === null || translation[field] === undefined) {
        return '';
      }

      return String(translation[field]).trim();
    },
    hasOptionTranslation(option, locale) {
      return !!this.getExactTranslationField(option, 'label', locale);
    },
    hasValueTranslation(value, locale) {
      return !!this.getExactTranslationField(value, 'label', locale);
    },
    getLocalizedTranslationField(target, field, fallbackField) {
      if (!target) {
        return '';
      }

      const translations = target.translations && typeof target.translations === 'object' && !Array.isArray(target.translations)
        ? target.translations
        : {};
      const candidates = [this.activeLocale, this.activeLocale.split('-')[0], 'hu'];

      for (let index = 0; index < candidates.length; index += 1) {
        const translation = translations[candidates[index]];
        const value = translation ? translation[field] : null;

        if (value !== null && value !== undefined && String(value).trim() !== '') {
          return value;
        }
      }

      return target[fallbackField || field] || target.key || target.value || '';
    },
    getLocalizedOptionLabel(option) {
      return this.getLocalizedTranslationField(option, 'label', 'label');
    },
    getLocalizedValueLabel(value) {
      return this.getLocalizedTranslationField(value, 'label', 'label');
    },
    normalizeOption(option) {
      const normalized = Object.assign({}, option || {});
      normalized.is_extra = normalized.is_extra === true || normalized.is_extra === 1 || normalized.is_extra === '1';
      normalized.is_locked = normalized.is_locked === true || normalized.is_locked === 1 || normalized.is_locked === '1';

      if (normalized.default_value === null || normalized.default_value === undefined) {
        normalized.default_value = '';
      }

      normalized.values = Array.isArray(normalized.values) ? normalized.values : [];
      return normalized;
    },
    normalizeMapping(mapping) {
      const normalized = Object.assign(initialMapping(), mapping || {});

      normalized.selected_options_json = this.normalizeSelectedOptions(normalized.selected_options_json);
      normalized.is_custom = normalized.is_custom === true || normalized.is_custom === 1 || normalized.is_custom === '1';
      normalized.is_active = !(normalized.is_active === false || normalized.is_active === 0 || normalized.is_active === '0');
      normalized._uid = normalized._uid || this.makeUid();

      return normalized;
    },
    normalizeSelectedOptions(value) {
      if (!value) {
        return {};
      }

      if (typeof value === 'string') {
        try {
          return JSON.parse(value) || {};
        } catch (e) {
          return {};
        }
      }

      if (Array.isArray(value)) {
        return {};
      }

      if (typeof value === 'object') {
        return Object.assign({}, value);
      }

      return {};
    },
    makeUid() {
      const uid = 'mapping_' + Date.now() + '_' + this.uidCounter;
      this.uidCounter += 1;
      return uid;
    },
    emitChange() {
      const payload = this.localMappings.map((mapping) => {
        const clean = Object.assign({}, mapping);
        delete clean._uid;

        clean.selected_options_json = this.cleanSelectedOptions(clean.selected_options_json);
        clean.base_item_id = this.baseItemId || clean.base_item_id;
        clean.target_item_id = clean.target_item_id || null;
        clean.target_reference = clean.target_reference || null;
        clean.is_custom = !!clean.is_custom;
        clean.is_active = !!clean.is_active;

        return clean;
      });

      this.$emit('input', payload);
    },
    clampMappingPage() {
      if (this.mappingPage < 1) {
        this.mappingPage = 1;
      }

      if (this.mappingPage > this.mappingPageCount) {
        this.mappingPage = this.mappingPageCount;
      }
    },
    goToLastMappingPage() {
      this.$nextTick(() => {
        this.mappingPage = this.mappingPageCount;
      });
    },
    cleanSelectedOptions(options) {
      const clean = {};
      const source = options || {};

      Object.keys(source).sort().forEach((key) => {
        if (source[key] !== null && source[key] !== '') {
          clean[key] = source[key];
        }
      });

      return clean;
    },
    getOptionValues(option) {
      if (!option || !Array.isArray(option.values)) {
        return [];
      }

      let values = option.values
        .filter((value) => {
          return value
            && value.value !== null
            && value.value !== ''
            && this.hasValueTranslation(value, this.activeLocale);
        })
        .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

      if (option.is_locked && option.default_value !== null && option.default_value !== undefined && option.default_value !== '') {
        values = values.filter((value) => String(value.value) === String(option.default_value));
      }

      return values;
    },
    getMappingOptionValue(mapping, key) {
      return mapping.selected_options_json ? mapping.selected_options_json[key] : null;
    },
    setMappingOptionValue(mapping, key, value) {
      if (!mapping.selected_options_json) {
        this.$set(mapping, 'selected_options_json', {});
      }

      if (value === null || value === '') {
        this.$delete(mapping.selected_options_json, key);
      } else {
        this.$set(mapping.selected_options_json, key, value);
      }

      if (this.isDuplicateMapping(mapping)) {
        Swal.fire({
          title: 'Duplicate mapping',
          text: 'This option combination already exists.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
      }

      this.emitChange();
    },
    buildMappingKey(options) {
      const clean = this.cleanSelectedOptions(options || {});
      return JSON.stringify(clean);
    },
    isDuplicateMapping(mapping) {
      const currentKey = this.buildMappingKey(mapping.selected_options_json);

      return this.localMappings.some((other) => {
        return other._uid !== mapping._uid && this.buildMappingKey(other.selected_options_json) === currentKey;
      });
    },
    generateMissingMappings() {
      if (!this.selectedMappingOptions.length) {
        Swal.fire({
          title: 'No mapping options selected',
          text: 'Please select at least one option used for mapping.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
        return;
      }

      const combinations = this.makeCombinations(this.selectedMappingOptions);

      if (!combinations.length) {
        Swal.fire({
          title: 'No combinations found',
          text: 'Selected options do not have selectable values.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
        return;
      }

      const existingKeys = this.localMappings.map((mapping) => this.buildMappingKey(mapping.selected_options_json));
      let addedCount = 0;

      combinations.forEach((combination) => {
        const key = this.buildMappingKey(combination);

        if (existingKeys.includes(key)) {
          return;
        }

        this.localMappings.push(this.normalizeMapping({
          base_item_id: this.baseItemId,
          selected_options_json: combination,
          target_item_id: null,
          target_reference: '',
          is_custom: this.isCustomCombination(combination),
          is_active: true,
        }));

        existingKeys.push(key);
        addedCount += 1;
      });

      this.emitChange();
      this.goToLastMappingPage();

      Swal.fire({
        title: 'Mappings generated',
        text: addedCount + ' missing mapping(s) added.',
        icon: 'success',
        confirmButtonText: 'OK',
      });
    },
    makeCombinations(options) {
      const lists = options.map((option) => {
        return this.getOptionValues(option).map((value) => {
          return {
            key: option.key,
            value: value.value,
          };
        });
      });

      if (lists.some((list) => !list.length)) {
        return [];
      }

      return lists.reduce((acc, list) => {
        const result = [];

        acc.forEach((item) => {
          list.forEach((entry) => {
            const next = Object.assign({}, item);
            next[entry.key] = entry.value;
            result.push(next);
          });
        });

        return result;
      }, [{}]);
    },
    isCustomCombination(combination) {
      return Object.keys(combination || {}).some((key) => {
        return String(combination[key]) === 'custom';
      });
    },
    addEmptyMapping() {
      const selected = {};

      this.selectedMappingKeys.forEach((key) => {
        selected[key] = null;
      });

      this.localMappings.push(this.normalizeMapping({
        base_item_id: this.baseItemId,
        selected_options_json: selected,
        target_item_id: null,
        target_reference: '',
        is_custom: false,
        is_active: true,
      }));

      this.emitChange();
      this.goToLastMappingPage();
    },
    duplicateMapping(mapping) {
      const clone = this.normalizeMapping(Object.assign({}, mapping, {
        id: null,
        target_item_id: null,
        target_reference: '',
      }));

      clone._uid = this.makeUid();
      this.localMappings.push(clone);
      this.emitChange();
      this.goToLastMappingPage();
    },
    async deleteMapping(mapping) {
      const result = await Swal.fire({
        title: 'Delete mapping?',
        text: 'This mapping will be removed from this item.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33',
      });

      if (!(result.isConfirmed || result.value)) {
        return;
      }

      this.localMappings = this.localMappings.filter((item) => item._uid !== mapping._uid);
      this.emitChange();
      this.clampMappingPage();
    },
    removeInactiveMappings() {
      this.localMappings = this.localMappings.filter((mapping) => mapping.is_active);
      this.emitChange();
      this.clampMappingPage();
    },
    handleTargetItemChange(mapping) {
      const item = this.findItemById(mapping.target_item_id);

      if (item) {
        mapping.target_reference = this.getItemReference(item);
      } else {
        mapping.target_reference = '';
      }

      this.emitChange();
    },
    findItemById(id) {
      return (this.itemCollection || []).find((item) => {
        return String(item.id) === String(id);
      });
    },
    getItemReference(item) {
      if (!item) {
        return '';
      }

      return item.reference || item.Reference || item.ref || item.code || '';
    },
    getItemDisplayName(item) {
      if (!item) {
        return '';
      }

      return item.ReferenceName || item.reference_name || item.name || item.reference || item.Reference || item.id;
    },
    ensureMappingsHaveSelectedKeys() {
      if (!this.localMappings.length || !this.selectedMappingKeys.length) {
        return;
      }

      let changed = false;

      this.localMappings.forEach((mapping) => {
        if (!mapping.selected_options_json) {
          this.$set(mapping, 'selected_options_json', {});
        }

        this.selectedMappingKeys.forEach((key) => {
          if (!Object.prototype.hasOwnProperty.call(mapping.selected_options_json, key)) {
            this.$set(mapping.selected_options_json, key, null);
            changed = true;
          }
        });
      });

      if (changed) {
        this.emitChange();
      }
    },
  },
};
</script>

<style scoped>
.item-web-option-mappings {
  color: #263238;
}

.web-mappings-card {
  border: 1px solid #e6ebf1 !important;
  border-radius: 14px !important;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.web-mappings-header {
  padding: 22px 24px 18px !important;
  border-bottom: 1px solid #edf1f5;
  background: linear-gradient(135deg, #fbfdff 0%, #f5f8fb 100%);
}

.web-mappings-title {
  color: #1f2d3d;
  font-weight: 600;
  line-height: 1.2;
}

.web-mappings-action,
.mapping-toolbar-button {
  border-radius: 9px;
}

.web-mappings-action {
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.18);
}

.web-mappings-body {
  padding: 22px 24px 24px !important;
}

.web-mappings-alert {
  margin-bottom: 18px;
  border-radius: 10px;
}

.web-mappings-panel {
  margin-bottom: 18px;
  padding: 18px 18px 8px;
  border: 1px solid #eef2f6;
  border-radius: 12px;
  background: #fafcff;
}

.mapping-toolbar-row {
  margin-top: 0;
}

.mapping-toolbar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding-top: 20px;
}

.mapping-table-wrap {
  overflow-x: auto;
  border: 1px solid #e7ecf2;
  border-radius: 12px;
  background: #ffffff;
}

.web-option-mapping-table {
  min-width: 1080px;
  overflow: hidden;
  border: 0;
  border-radius: 12px;
  background: #ffffff;
}

.web-option-mapping-table >>> table {
  table-layout: fixed;
}

.web-option-mapping-table th,
.web-option-mapping-table td {
  vertical-align: middle;
}

.web-option-mapping-table th {
  height: 42px;
  padding: 0 16px !important;
  color: #334155;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  background: #f8fafc;
  border-bottom: 1px solid #e7ecf2 !important;
}

.web-option-mapping-table td {
  height: 66px;
  padding: 9px 16px !important;
  color: #37474f;
  border-bottom: 1px solid #edf1f5 !important;
}

.web-option-mapping-table tbody tr {
  transition: background-color 0.16s ease;
}

.web-option-mapping-table tbody tr:hover {
  background: #fbfdff;
}

.mapping-option-column {
  width: 150px;
}

.target-item-column {
  width: 340px;
  min-width: 340px;
}

.target-reference-column {
  width: 210px;
  min-width: 210px;
}

.small-column {
  width: 92px;
}

.action-column {
  width: 110px;
}

.mapping-control {
  font-size: 13px;
}

.mapping-control >>> .v-input__control,
.mapping-control >>> .v-input__slot {
  min-height: 40px !important;
}

.mapping-control >>> .v-input__slot {
  margin-bottom: 0 !important;
  align-items: center;
}

.mapping-control >>> .v-select__selection,
.mapping-control >>> input {
  color: #111827;
  font-size: 13px;
  line-height: 1.35;
}

.mapping-control >>> .v-input__append-inner {
  align-self: center;
  margin-top: 0 !important;
  padding-left: 6px;
}

.mapping-control >>> .v-input__append-inner .v-icon {
  color: #607d8b !important;
  font-size: 18px !important;
}

.mapping-option-cell .mapping-control >>> .v-select__selection {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mapping-control--target >>> .v-select__slot {
  align-items: center;
}

.mapping-control--target >>> .v-select__selection {
  display: -webkit-box;
  max-width: 100%;
  max-height: 38px;
  overflow: hidden;
  margin: 0;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  white-space: normal;
}

.mapping-control--reference >>> input {
  font-weight: 500;
}

.mapping-toggle {
  display: inline-flex;
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.mapping-flag-cell >>> .v-input--selection-controls__input {
  margin-right: 0;
}

.mapping-flag-cell >>> .v-input__slot {
  justify-content: center;
}

.mapping-actions-cell {
  white-space: nowrap;
}

.mapping-pagination-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 4px 2px;
}

.mapping-pagination-info {
  min-width: 170px;
}

.mapping-per-page-select {
  max-width: 150px;
}

.mapping-pagination {
  margin-left: 0;
}

.mapping-action-icon {
  padding: 6px;
  border-radius: 8px;
  color: #607d8b;
  transition: background-color 0.16s ease, color 0.16s ease, transform 0.16s ease;
}

.mapping-action-icon:hover {
  color: #1976d2;
  background: #eef6ff;
  transform: translateY(-1px);
}

.mapping-action-icon--danger:hover {
  color: #d32f2f;
  background: #fff1f1;
}

.mapping-row-inactive {
  opacity: 0.55;
}

.mapping-row-inactive td {
  background: #fafafa;
}

.empty-mappings-cell {
  color: #82909d;
  background: #fcfdff;
}

.text-muted {
  color: #82909d;
}

.web-mappings-divider {
  margin: 18px 0 !important;
  border-color: #edf1f5 !important;
}

.web-mappings-note {
  padding: 12px 14px;
  border: 1px solid #edf1f5;
  border-radius: 10px;
  background: #fafcff;
  line-height: 1.6;
}

@media (max-width: 600px) {
  .web-mappings-header,
  .web-mappings-body {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .web-mappings-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .web-mappings-action {
    margin-top: 14px;
  }

  .web-mappings-panel {
    padding: 16px 12px 6px;
  }

  .mapping-toolbar-actions {
    align-items: stretch;
    flex-direction: column;
    padding-top: 4px;
  }

  .mapping-toolbar-button {
    width: 100%;
    margin: 0 0 8px !important;
  }
}
</style>
